import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StyleSheet, Text, View } from './components/base_components';
import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';
import UserSelect from './components/user_select';
import DeviceSelect from './components/device_select';

import DeviceStore from './stores/device_store';
import MediaGrantAdminStore from './stores/media_grant_admin_store';
import MediaGrantStore from './stores/media_grant_store';
import UserStore from './stores/user_store';
import UserAdminStore from './stores/user_admin_store';
import VolumeStore from './stores/volume_store';

import { relativeDate } from './tools/util';

const styles = StyleSheet.create({
  volumePermissionScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  empty: {
    margin: '2em',
    fontSize: '1.5em',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  input: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
  addBox: {
    marginTop: 0,
  },
});

export default function VolumeGrantScreen(props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = UserStore.useOrg();
  const user = UserStore.useUser();
  const volume_id = parseInt(params.volume_id ?? '0', 10) || 0;
  const grant_name = params.grant_name ?? '';
  const org_id = org?.org_id;
  const volume = VolumeStore.useVolume(org_id, volume_id);
  const media_grant = MediaGrantAdminStore.useMediaGrant(org_id, grant_name);
  const media_grant_id = media_grant?.media_grant_id;
  const [add_device, setAddDevice] = React.useState<number | ''>('');
  const [add_user, setAddUser] = React.useState<number | ''>('');
  const [user_selected, setUserSelected] = React.useState([]);
  const [device_selected, setDeviceSelected] = React.useState([]);
  const [updates, setUpdates] = React.useState<any>({});
  const navigate = useNavigate();

  React.useEffect(() => {
    if (org_id) {
      MediaGrantAdminStore.fetch(org_id);
      UserAdminStore.fetch(org_id);
      VolumeStore.fetch(org_id);
    }
  }, [org_id]);

  const selected_user = media_grant?.user_list?.[user_selected[0]];
  const selected_user_id = selected_user?.user_id;
  const selected_device = media_grant?.device_list?.[device_selected[0]];
  const selected_device_id = selected_device?.device_id;
  const user_rows = media_grant?.user_list?.map?.((u) => ({
    list: [u.email, u.user_name || '-'],
  }));
  const device_rows = media_grant?.device_list?.map?.((d) => ({
    list: [
      d.device_name,
      d.is_disabled ? 'Disabled' : 'Enabled',
      relativeDate(d.last_seen) || '-',
    ],
  }));

  function _onAddUserPress() {
    if (add_user && setBusy()) {
      const opts = { org_id, media_grant_id, user_id: add_user };
      MediaGrantAdminStore.addUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add user, please try again.');
        } else {
          if (opts.user_id === user?.user_id) {
            MediaGrantStore.fetch(org_id);
          }
          setAddUser('');
        }
      });
    }
  }
  function _onAddDevicePress() {
    if (add_device && setBusy()) {
      const opts = { org_id, media_grant_id, device_id: add_device };
      DeviceStore.addMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add device, please try again.');
        } else {
          MediaGrantAdminStore.fetch(org_id);
          setAddDevice('');
        }
      });
    }
  }
  function _onRemoveUserPress() {
    if (selected_user_id && setBusy()) {
      const opts = { org_id, media_grant_id, user_id: selected_user_id };
      MediaGrantAdminStore.removeUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to remove user, please try again.');
        } else {
          if (opts.user_id === user?.user_id) {
            MediaGrantStore.fetch(org_id);
          }
          setUserSelected([]);
        }
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts = { org_id, media_grant_id, ...updates };
      if (opts.is_disabled !== undefined) {
        opts.is_disabled = opts.is_disabled === 'Disabled' ? 1 : 0;
      }
      MediaGrantAdminStore.update(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert('Name already in use.');
        } else if (err) {
          window.alert('Update failed, please try again.');
        } else {
          if (updates.grant_name) {
            MediaGrantStore.fetch(org_id);
            navigate(`/volume/${volume_id}/grant/${updates.grant_name}`, {
              replace: true,
            });
          }
          complete?.();
        }
      });
    }
  }
  function _onRemovePress() {
    const yes = window.confirm('Are you sure you want to delete this grant?');
    if (yes && setBusy()) {
      const opts = { org_id, media_grant_id };
      MediaGrantAdminStore.del(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert(
            'This grant has provisioned devices. You may disable but not delete this grant.'
          );
        } else if (err) {
          window.alert('Remove failed, please try again.');
        } else {
          MediaGrantStore.fetch(org_id);
          navigate(`/volume/${volume_id}/permission`, { replace: true });
        }
      });
    }
  }
  function _onRemoveDevicePress() {
    if (selected_device_id && setBusy()) {
      const opts = { org_id, media_grant_id, device_id: selected_device_id };
      DeviceStore.deleteMediaGrant(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert(
            'This device is already provisioned, you may not delete the grant, but you can disable it.'
          );
        } else if (err) {
          window.alert('Remove failed, please try again.');
        } else {
          MediaGrantAdminStore.fetch(org_id);
        }
      });
    }
  }
  function _onEnableDevicePress() {
    if (selected_device_id && setBusy()) {
      const opts = { org_id, media_grant_id, device_id: selected_device_id };
      DeviceStore.enableMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to enable device grant, please try again.');
        } else {
          setDeviceSelected([]);
          MediaGrantAdminStore.fetch(org_id);
        }
      });
    }
  }
  function _onDisableDevicePress() {
    if (selected_device_id && setBusy()) {
      const opts = { org_id, media_grant_id, device_id: selected_device_id };
      DeviceStore.disableMediaGrant(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to disable device grant, please try again.');
        } else {
          setDeviceSelected([]);
          MediaGrantAdminStore.fetch(org_id);
        }
      });
    }
  }
  return (
    <View style={styles.volumePermissionScreen}>
      <Breadcrumb
        list={[
          { text: 'Volumes', url: '/volume' },
          { text: volume?.volume_name, url: `/volume/${volume_id}` },
          { text: 'Permissions', url: `/volume/${volume_id}/permission` },
          media_grant?.grant_name,
        ]}
      />
      <TitleActionLine title={media_grant?.grant_name}>
        <TextButton
          style={styles.actionButton}
          type='danger'
          text='Delete Grant'
          onPress={_onRemovePress}
        />
      </TitleActionLine>
      <BoxPropList
        title='Grant Details'
        editable={true}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={[
          [
            {
              key: 'grant_name',
              label: 'Container Name',
              value: media_grant?.grant_name,
              input: {
                placeholder: 'Name',
              },
            },
            {
              label: 'Prefix Path',
              value: media_grant?.prefix_path,
            },
            {
              key: 'is_disabled',
              label: 'Status',
              value: media_grant?.is_disabled ? 'Disabled' : 'Enabled',
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
          ],
        ]}
      />
      <StandardBoxList
        title='Users'
        detail={user_rows?.length ?? 0}
        headers={['Email', 'User Name']}
        singleSelect={true}
        selected={user_selected}
        onChange={setUserSelected}
        buttons={
          <TextButton
            style={styles.actionButton}
            text='Remove'
            disabled={user_selected.length === 0}
            onPress={_onRemoveUserPress}
          />
        }
        rows={user_rows}
      >
        {user_rows?.length === 0 ? (
          <Text style={styles.empty}>No users found.</Text>
        ) : null}
      </StandardBoxList>
      <StandardBoxList style={styles.addBox} title='Add User'>
        <View style={styles.dialog}>
          <Text style={styles.text}>Add a user to this grant.</Text>
          <UserSelect
            style={styles.input}
            orgId={org?.org_id}
            excludeList={media_grant?.user_list}
            placeholder='Select User'
            value={add_user}
            onChange={setAddUser}
          />
          <TextButton
            style={styles.button}
            text='Add User'
            onPress={_onAddUserPress}
          />
        </View>
      </StandardBoxList>
      <StandardBoxList
        title='Devices'
        detail={device_rows?.length ?? 0}
        headers={['Device Name', 'State', 'Last Seen']}
        singleSelect={true}
        selected={device_selected}
        onChange={setDeviceSelected}
        buttons={
          <>
            <TextButton
              style={styles.actionButton}
              text='Enable'
              disabled={selected_device?.is_disabled !== 1}
              onPress={_onEnableDevicePress}
            />
            <TextButton
              style={styles.actionButton}
              text='Disable'
              disabled={selected_device?.is_disabled !== 0}
              onPress={_onDisableDevicePress}
            />
            <TextButton
              style={styles.actionButton}
              text='Delete'
              type='danger'
              disabled={!selected_device}
              onPress={_onRemoveDevicePress}
            />
          </>
        }
        rows={device_rows}
      >
        {device_rows?.length === 0 ? (
          <Text style={styles.empty}>No devices found.</Text>
        ) : null}
      </StandardBoxList>
      <StandardBoxList style={styles.addBox} title='Add Device'>
        <View style={styles.dialog}>
          <Text style={styles.text}>Add a device to this grant.</Text>
          <DeviceSelect
            style={styles.input}
            orgId={org_id}
            excludeList={media_grant?.device_list}
            placeholder='Select Device'
            value={add_device}
            onChange={setAddDevice}
          />
          <TextButton
            style={styles.button}
            text='Add Device'
            onPress={_onAddDevicePress}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
